'use client';

import { useState, useEffect } from 'react';
import { AlertCircle, Upload, CheckCircle2 } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { useUser } from '@/contexts/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChatSessions,
  useChatMessages,
  useCreateChatSession,
  useDeleteChatSession,
  useSendMessage,
  useCommitUpload,
  useOptimisticMessage,
  chatKeys,
} from '@/hooks/useChatApi';
import { useUploadFileStream } from '@/hooks/useUploadFileStream';
import { useChatJobPolling } from '@/hooks/useChatJobPolling';
import { ChatSidebar } from './ChatSidebar';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { CompanySearchDialog } from './CompanySearchDialog';
import { ConfirmUploadBanner } from './ConfirmUploadBanner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { useSnackbar } from '@/providers/SnackbarProvider';

/**
 * AI Upload chat container — company-centric conversational upload.
 *
 * Flow:
 * 1. User selects a company (CompanySearchDialog)
 * 2. Session created tied to that company
 * 3. User uploads Excel file -> Cortex analyzes and previews it
 * 4. User chats with Cortex about the data
 * 5. User clicks "Confirm Upload" -> triggers the benchmarking pipeline
 */
export function AIUploadChatContainer() {
  const { selectedTenantId } = useTenant();
  const { userId, isLoading: userLoading } = useUser();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [hasPendingFile, setHasPendingFile] = useState(false);
  const [pipelineJustCompleted, setPipelineJustCompleted] = useState(false);

  // Poll for async job status
  const { data: jobStatus } = useChatJobPolling(activeJobId, activeJobId !== null);

  // Fetch sessions filtered to ai_upload type
  const { data: sessionsData } = useChatSessions(userId, selectedTenantId, 20, 0, 'ai_upload');

  // Fetch messages for selected session
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useChatMessages(userId, selectedTenantId, selectedSessionId);

  // Mutations
  const createSession = useCreateChatSession();
  const deleteSession = useDeleteChatSession();
  const sendMessage = useSendMessage();
  const {
    isStreaming,
    statusMessage: streamStatusMessage,
    streamedText,
    isCodeExecuting,
    error: streamError,
    usage: streamUsage,
    upload: streamUpload,
  } = useUploadFileStream();
  const commitUpload = useCommitUpload();

  // Optimistic message helper
  const addOptimisticMessage = useOptimisticMessage(selectedTenantId, selectedSessionId);

  // Track pending file state from messages response
  useEffect(() => {
    if (messagesData) {
      setHasPendingFile(messagesData.has_pending_file ?? false);
    }
  }, [messagesData]);

  // Reset pipeline flag when switching sessions
  useEffect(() => {
    setPipelineJustCompleted(false);
  }, [selectedSessionId]);

  // Auto-select first session
  useEffect(() => {
    if (sessionsData?.sessions?.length && !selectedSessionId) {
      setSelectedSessionId(sessionsData.sessions[0].id);
    }
  }, [sessionsData?.sessions?.length, selectedSessionId]);

  // Clear session when deleted
  useEffect(() => {
    if (selectedSessionId && sessionsData?.sessions && !sessionsData.sessions.find(s => s.id === selectedSessionId)) {
      setSelectedSessionId(sessionsData.sessions.length > 0 ? sessionsData.sessions[0].id : null);
    }
  }, [sessionsData, selectedSessionId]);

  // Handle stale session (e.g. 404 from messages endpoint)
  useEffect(() => {
    if (messagesError && selectedSessionId) {
      setSelectedSessionId(null);
    }
  }, [messagesError, selectedSessionId]);

  // Handle async job completion
  useEffect(() => {
    if (jobStatus?.status === 'complete' && activeJobId && selectedTenantId && jobStatus.result?.session_id) {
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(selectedTenantId, jobStatus.result.session_id),
      });
      if (jobStatus.result.session_id !== selectedSessionId) {
        setSelectedSessionId(jobStatus.result.session_id);
      }
      setActiveJobId(null);
      setHasPendingFile(false);
      setPipelineJustCompleted(true);
    }
    if (jobStatus?.status === 'failed' && activeJobId && selectedTenantId) {
      if (selectedSessionId) {
        queryClient.invalidateQueries({
          queryKey: chatKeys.messages(selectedTenantId, selectedSessionId),
        });
      }
      setActiveJobId(null);
    }
  }, [jobStatus?.status, jobStatus?.result?.session_id, activeJobId, selectedTenantId, selectedSessionId, queryClient]);

  // Handlers
  const handleNewSession = () => {
    setShowCompanySearch(true);
  };

  const handleCompanySelected = async (company: { dbId: number; shortName: string }) => {
    if (!selectedTenantId || !userId) return;
    try {
      const session = await createSession.mutateAsync({
        userId,
        tenantId: selectedTenantId,
        agentType: 'ai_upload',
        companyId: company.dbId,
        companyName: company.shortName,
      });
      setSelectedSessionId(session.id);
    } catch (error) {
      console.error('Failed to create session:', error);
      const message = error instanceof Error ? error.message : String(error);
      showSnackbar(message || 'Failed to create chat session', 'error', false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!selectedTenantId || !userId) return;
    setDeletingSessionId(sessionId);
    try {
      await deleteSession.mutateAsync({
        userId,
        tenantId: selectedTenantId,
        sessionId,
      });
    } catch (error) {
      console.error('Failed to delete session:', error);
      const message = error instanceof Error ? error.message : String(error);
      showSnackbar(message || 'Failed to delete chat session', 'error', false);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedTenantId || !selectedSessionId || !userId) return;

    addOptimisticMessage(`Uploaded file: **${file.name}**`);

    const result = await streamUpload({
      userId,
      tenantId: selectedTenantId,
      sessionId: selectedSessionId,
      file,
    });

    if (result?.has_pending_file) {
      setHasPendingFile(true);
    }

    if (streamError) {
      showSnackbar(streamError || 'Failed to upload file', 'error', false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!selectedTenantId || !selectedSessionId || !userId) return;

    addOptimisticMessage(content);

    try {
      await sendMessage.mutateAsync({
        userId,
        tenantId: selectedTenantId,
        sessionId: selectedSessionId,
        content,
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      const message = error instanceof Error ? error.message : String(error);
      showSnackbar(message || 'Failed to send message', 'error', false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedTenantId || !selectedSessionId || !userId) return;

    try {
      const result = await commitUpload.mutateAsync({
        userId,
        tenantId: selectedTenantId,
        sessionId: selectedSessionId,
      });

      if (result.is_async && result.job_id) {
        setActiveJobId(result.job_id);
      }
    } catch (error) {
      console.error('Failed to commit upload:', error);
      const message = error instanceof Error ? error.message : String(error);
      showSnackbar(message || 'Failed to start processing', 'error', false);
    }
  };

  const handleCancelUpload = () => {
    setHasPendingFile(false);
    // TODO: optionally call backend to clear the pending file
  };

  // Loading states
  if (userLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Alert variant="default" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Loading</AlertTitle>
          <AlertDescription>Loading user information...</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Unable to load user information. Please refresh the page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedTenantId) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Alert variant="default" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Select a Tenant</AlertTitle>
          <AlertDescription>Please select a tenant from the sidebar to start.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const sessions = sessionsData?.sessions ?? [];
  const messages = messagesData?.messages ?? [];
  const isProcessing = isStreaming || sendMessage.isPending;
  const selectedSession = sessions.find(s => s.id === selectedSessionId);

  // Determine if the session has already been processed (pipeline ran previously)
  const hasContainer = !!selectedSession?.anthropic_container_id;
  const pipelineCompleted =
    pipelineJustCompleted || (hasContainer && !hasPendingFile && messages.length > 0 && !activeJobId);

  return (
    <div className="flex h-full bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Company Search Dialog */}
      <CompanySearchDialog
        open={showCompanySearch}
        onClose={() => setShowCompanySearch(false)}
        onSelect={handleCompanySelected}
      />

      {/* Sidebar */}
      <ChatSidebar
        sessions={sessions}
        selectedSessionId={selectedSessionId}
        onSelectSession={setSelectedSessionId}
        onNewSession={handleNewSession}
        onDeleteSession={handleDeleteSession}
        isCreating={createSession.isPending}
        isDeleting={deletingSessionId}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Session header — shows company name and state for resumed sessions */}
        {selectedSession && (
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-700 truncate">
                {selectedSession.company_name ?? 'AI Upload'}
              </span>
              {pipelineCompleted && (
                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Pipeline complete
                </span>
              )}
              {hasPendingFile && !activeJobId && (
                <span className="inline-flex items-center text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                  File pending review
                </span>
              )}
              {activeJobId && (
                <span className="inline-flex items-center text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                  Pipeline running
                </span>
              )}
              {streamUsage && (
                <span className="text-xs text-gray-500 ml-2">
                  ~${((streamUsage.input * 3 + streamUsage.output * 15) / 1_000_000).toFixed(2)} API cost
                </span>
              )}
            </div>
          </div>
        )}

        {/* Messages or empty state */}
        {messages.length === 0 && !isLoadingMessages ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <EmptyState
              icon={<Upload />}
              title="Welcome to AI Upload"
              description="Select a company and upload your deal assessment Excel file (.xlsx). Cortex will analyze it before you confirm processing."
            />
          </div>
        ) : (
          <ChatMessageList
            messages={messages}
            isLoading={isLoadingMessages}
            isTyping={isProcessing}
            jobStatus={activeJobId ? (jobStatus ?? null) : null}
            streamingMessage={
              isStreaming
                ? {
                    content: streamedText,
                    statusMessage: streamStatusMessage,
                    isCodeExecuting,
                  }
                : null
            }
          />
        )}

        {/* Confirm Upload Banner */}
        {hasPendingFile && !activeJobId && (
          <ConfirmUploadBanner
            onConfirm={handleConfirmUpload}
            onCancel={handleCancelUpload}
            isProcessing={commitUpload.isPending}
            jobStatus={null}
          />
        )}

        {/* Pipeline Progress Banner */}
        {activeJobId && jobStatus && (
          <ConfirmUploadBanner onConfirm={() => {}} onCancel={() => {}} isProcessing={true} jobStatus={jobStatus} />
        )}

        {/* Input */}
        <ChatInput
          onSend={handleSendMessage}
          onFileUpload={handleFileUpload}
          showFileUpload={true}
          isLoading={isProcessing}
          disabled={!selectedSessionId}
          uploadedFiles={messagesData?.files ?? []}
          placeholder={
            !selectedSessionId
              ? 'Select a company to get started'
              : pipelineCompleted
                ? 'Ask follow-up questions about this data...'
                : hasPendingFile
                  ? 'Ask questions about the file before confirming...'
                  : 'Upload an Excel file or ask Cortex about the data...'
          }
        />
      </div>
    </div>
  );
}
